// assets/js/components/footer/accessFilters.js
import mountFarloRangeCalendar from './rangeCalendar';
import {
	debounce,
	has3Chars,
	initFilterBar,
	updateAllFiltersCount,
	updateClearChip,
	wireCalendarEnable,
	markScopeDirty,
	paintChoiceActives,
	toQS,
	cleanParams,
	seedFormFromURL
} from './filtersCore';
import { createFilterTracker } from './filterAnalytics';

export default function accessFilters() {
	const bar = document.querySelector('[data-access-filterbar]');
	const list = document.querySelector('[data-access-results]') || document.querySelector('.shows-access-grid');
	if (!bar || !list) {
		return;
	}

	const form = bar;
	const filterTracker = createFilterTracker('access');

	// hidden CSV for time
	let timeHidden = form.querySelector('input[name="time"]');
	if (!timeHidden) {
		timeHidden = document.createElement('input'); timeHidden.type = 'hidden'; timeHidden.name = 'time'; form.appendChild(timeHidden);
	}

	const DEFAULTS = { from: '', to: '', time: '', sort: 'name_asc' };
	const SORT_MAP = { 'name-atz': 'name_asc', 'name-zta': 'name_desc', 'date-old': 'date_old', 'date-new': 'date_new' };
	const normaliseSort = (v) => SORT_MAP[ v ] || v || 'name_asc';

	const ACCESS_TZ = (document.querySelector('[data-access-results], .shows-access-grid')?.getAttribute('data-tz')) || 'Europe/London';
	const hourInZone = (ms, tz = ACCESS_TZ) => Number(new Date(ms).toLocaleString('en-GB', { timeZone: tz, hour12: false, hour: '2-digit' }));
	const isMatineeHour = (h) => h < 17;
	const toMs = (ts) => (ts > 2e10 ? ts : ts * 1000);

	const TYPE_SHORT_MAP = {
		'audio-described': 'ad',
		'captioned': 'cap',
		'british sign language': 'bsl',
		'Sign Language': 'bsl',
		'relaxed': 'relaxed'
	};

	const pushUrl = (base) => {
		const qs = toQS(base);
		history.pushState({ qs }, '', qs ? `${ location.pathname }?${ qs }` : location.pathname);
	};

	const setTriggerActive = (key, active) => {
		bar.querySelectorAll(`[data-open="${ key }"]`).forEach((btn) => {
			btn.classList.toggle('is-active', !!active);
			btn.setAttribute('aria-pressed', active ? 'true' : 'false');
		});
	};
	const isDateActive = () => !!(form.querySelector('input[name="from"]')?.value || form.querySelector('input[name="to"]')?.value);
	const isTimeActive = () => (form.querySelector('input[name="time"]')?.value || '').trim().length > 0;
	const isTypeActive = () => Array.from(form.querySelectorAll('input[name="type"]:checked, input[name="type[]"]:checked')).length > 0;
	const setAllTriggersFromForm = () => {
		setTriggerActive('date', isDateActive());
		setTriggerActive('time', isTimeActive());
		setTriggerActive('type', isTypeActive());
	};

	function updateDateChipActive() {
		const fromVal = form.querySelector('input[name="from"]')?.value?.trim() || '';
		const toVal = form.querySelector('input[name="to"]')?.value?.trim() || '';
		const active = !!(fromVal || toVal);
		bar.querySelectorAll('[data-open="date"]').forEach((btn) => {
			btn.classList.toggle('is-active', active);
			btn.setAttribute('aria-pressed', active ? 'true' : 'false');
		});
	}

	function syncInlineDatesToModal(modal) {
		if (!modal) {
			return;
		}
		const mFrom = modal.querySelector('[data-modal-from]');
		const mTo = modal.querySelector('[data-modal-to]');
		const from = form.querySelector('input[name="from"]');
		const to = form.querySelector('input[name="to"]');
		if (mFrom && from) {
			mFrom.value = from.value || '';
		}
		if (mTo && to) {
			mTo.value = to.value || '';
		}
	}

	function writeTimeCSV(values) {
		const order = ['matinee', 'evening'];
		const uniq = Array.from(new Set(values.map((v) => v.trim().toLowerCase()).filter(Boolean)));
		uniq.sort((a, b) => order.indexOf(a) - order.indexOf(b));
		timeHidden.value = uniq.join(',');
		setAllTriggersFromForm();
	}

	function baseFromAccessForm() {
		const base = {};
		const get = (n) => form.querySelector(`[name="${ n }"]`)?.value || '';
		const hasDate = form.querySelector('input[name="from"]') || form.querySelector('input[name="to"]');
		if (hasDate) {
			const from = get('from'); const to = get('to');
			if (from) {
				base.from = from;
			} if (to) {
				base.to = to;
			}
		}
		const timeCsv = get('time'); if (timeCsv) {
			base.time = timeCsv;
		}

		const types = Array.from(form.querySelectorAll('input[name="type"]:checked, input[name="type[]"]:checked'))
			.map((i) => (i.value || i.dataset.key || '').trim()).filter(Boolean);
		if (types.length) {
			base.type = [...new Set(types)].join(',');
		}

		const rawSort = form.querySelector('select[name="sort"], input[name="sort"]:checked')?.value || 'name-atz';
		const norm = SORT_MAP[ rawSort ] || rawSort;
		if (norm !== 'name_asc') {
			base.sort = norm;
		}

		return base;
	}

	function renderLocal() {
		const cards = Array.from(list.querySelectorAll('.shows-access-grid-item'));

		// query
		const qEl = form.querySelector('input[name="q"], #q');
		const query = has3Chars(qEl?.value) ? qEl.value.trim().toLowerCase() : '';

		// dates
		const fromVal = form.querySelector('input[name="from"]')?.value || '';
		const toVal = form.querySelector('input[name="to"]')?.value || '';
		const fromTs = fromVal ? Date.parse(fromVal) : null;
		const toTs = toVal ? Date.parse(toVal) : null;

		// time
		const timeCsv = form.querySelector('input[name="time"]')?.value || '';
		const timeSet = new Set(timeCsv.split(',').map((s) => s.trim().toLowerCase()).filter(Boolean));

		// types
		const chosenTypes = Array.from(form.querySelectorAll('input[name="type"]:checked, input[name="type[]"]:checked'))
			.map((i) => (i.value || i.dataset.key || '').trim()).filter(Boolean);
		const chosenTypeSet = new Set(chosenTypes);

		const rowMatchesTime = (row) => {
			if (!timeSet.size) {
				return true;
			}
			const rawTs = Number(row.getAttribute('data-ap-ts') || 0);
			if (!rawTs) {
				return true;
			}
			const h = hourInZone(toMs(rawTs));
			return isMatineeHour(h) ? timeSet.has('matinee') : timeSet.has('evening');
		};

		const earliestTsInCard = (card) => {
			let min = Infinity;
			card.querySelectorAll('[data-ap-ts]').forEach((r) => {
				const ts = Number(r.getAttribute('data-ap-ts') || 0);
				if (ts && ts < min) {
					min = ts;
				}
			});
			return Number.isFinite(min) ? min : Infinity;
		};

		cards.forEach((card) => {
			const title = (card.getAttribute('data-title') || '').toLowerCase();
			const venue = (card.getAttribute('data-venue') || '').toLowerCase();
			const matchQuery = !query || title.includes(query) || venue.includes(query);

			let anyVisible = false;

			Array.from(card.querySelectorAll('[data-ap-ts]')).forEach((row) => {
				const rawTs = Number(row.getAttribute('data-ap-ts') || 0);
				const tsMs = rawTs ? toMs(rawTs) : 0;

				const typesAttr = (row.getAttribute('data-ap-type') || '')
					.split(',').map((s) => s.trim()).filter(Boolean);

				const okType = !chosenTypeSet.size || typesAttr.some((t) => {
					if (chosenTypeSet.has(t)) {
						return true;
					}
					// allow full <-> short matching
					const lowerFull = (t || '').toLowerCase();
					const short = TYPE_SHORT_MAP[ lowerFull ];
					if (short && chosenTypeSet.has(short)) {
						return true;
					}
					// also map reverse (if chosenTypes has full but row carries short)
					const fullFromShort = Object.entries(TYPE_SHORT_MAP).find(([, sh]) => sh === t)?.[ 0 ];
					return fullFromShort ? chosenTypeSet.has(fullFromShort) : false;
				});

				const after = fromTs ? tsMs >= fromTs : true;
				const before = toTs ? tsMs <= (toTs + 86399999) : true;
				const okTime = rowMatchesTime(row);

				const show = okType && after && before && okTime && matchQuery;
				row.style.display = show ? '' : 'none';
				if (show) {
					anyVisible = true;
				}
			});

			card.querySelectorAll('[data-type-key]').forEach((sec) => {
				const hasVisibleRows = Array.from(sec.querySelectorAll('[data-ap-ts]')).some((r) => r.style.display !== 'none');
				sec.style.display = hasVisibleRows ? '' : 'none';
			});

			card.style.display = anyVisible ? '' : 'none';
		});

		// sort visible cards
		const visible = cards.filter((c) => c.style.display !== 'none');
		const sortKey = normaliseSort(
			form.querySelector('input[name="sort"]:checked')?.value ||
			form.querySelector('select[name="sort"]')?.value ||
			'name_asc'
		);

		const collator = new Intl.Collator('en', { sensitivity: 'base' });
		const byTitle = (a, b) => collator.compare(
			a.getAttribute('data-sort-title') || a.getAttribute('data-title') || '',
			b.getAttribute('data-sort-title') || b.getAttribute('data-title') || ''
		);
		const bySoon = (a, b) => earliestTsInCard(a) - earliestTsInCard(b);

		switch (sortKey) {
			case 'date_new': visible.sort((a, b) => bySoon(b, a)); break;
			case 'date_old': visible.sort(bySoon); break;
			case 'name_desc': visible.sort((a, b) => byTitle(b, a)); break;
			default: visible.sort(byTitle); break;
		}
		visible.forEach((c) => list.appendChild(c));
	}

	// Build the params object passed to the analytics tracker. baseFromAccessForm
	// does not carry the free-text query (it filters the DOM directly), so the
	// search box value is folded in here, mirroring the has3Chars() gate that
	// renderLocal() applies before treating q as an active filter.
	const trackParams = () => {
		const qVal = (form.querySelector('input[name="q"], #q')?.value || '').trim();
		return { ...cleanParams(baseFromAccessForm(), DEFAULTS), q: has3Chars(qVal) ? qVal : '' };
	};
  	/**
	 * Reflects the active state of the "Filter & Sort" button based on whether
	 * any filters are currently applied. Uses baseFromAccessForm() as the source
	 * of truth — the same data that drives the Clear All chip — rather than
	 * relying on a DOM query within updateAllFiltersCount, which can miss the
	 * type chip state on initial page load when the URL seeds the form.
	 */
	function syncAllFiltersButton() {
		const base = cleanParams(baseFromAccessForm(), DEFAULTS);
		const hasFilters = Object.keys(base).length > 0;
		bar.querySelectorAll('[data-open="all"]').forEach((btn) => {
			btn.classList.toggle('is-active', hasFilters);
			btn.setAttribute('aria-pressed', hasFilters ? 'true' : 'false');
			const countEl = btn.querySelector('[data-all-count]');
			if (countEl) {
				let count = 0;
				if (base.type) {
					count += base.type.split(',').filter(Boolean).length;
				}
				if (base.time) {
					count ++;
				}
				if (base.from || base.to) {
					count ++;
				}
				if (base.max !== undefined && base.max !== '') {
					count ++;
				}
				if (base.sort && base.sort !== 'name_asc') {
					count ++;
				}
				countEl.textContent = count > 0 ? `(${ count })` : '';
			}
		});
	}


	function localRenderAndUi(push = true) {
		const base = cleanParams(baseFromAccessForm(), DEFAULTS);
		filterTracker.track(trackParams());
		if (push) {
			pushUrl(base);
		}
		renderLocal();
		paintChoiceActives(form);
		updateAllFiltersCount(bar, form, { multiCsvKeys: new Set(['time', 'type']), perItemKeys: new Set(['type']) });
		updateClearChip(bar, base, { sort: 'name_asc' });
		setAllTriggersFromForm();
		updateDateChipActive();
		syncAllFiltersButton();
	}

	initFilterBar({
		bar, form, list,
		multi: new Set(['time', 'type']),
		endpoint: null,
		defaults: DEFAULTS,
		paramsFromForm: baseFromAccessForm,
		render: () => localRenderAndUi(true),
		onBeforeOpenScope(key, { modal, panel }) {
			if (key === 'date' && panel) {
				const container = panel.querySelector('[data-range-calendar]');
				const from = form.querySelector('input[name="from"]');
				const to = form.querySelector('input[name="to"]');
				if (container && !container.dataset.calBound && from && to) {
					wireCalendarEnable(panel, container, from, to, () => {
						mountFarloRangeCalendar(container, from, to, 1);
					});
				}
			}
			if (key === 'all' && modal) {
				const container = modal.querySelector('[data-range-calendar="modal"]');
				const mFrom = modal.querySelector('[data-modal-from]') || form.querySelector('input[name="from"]');
				const mTo = modal.querySelector('[data-modal-to]') || form.querySelector('input[name="to"]');
				// dates
				syncInlineDatesToModal(modal);
				if (container && !container.dataset.calBound && mFrom && mTo) {
					wireCalendarEnable(modal, container, mFrom, mTo, () => {
						const mq = matchMedia('(min-width:768px)');
						const mount = () => {
							container.innerHTML = '';
							mountFarloRangeCalendar(container, mFrom, mTo, mq.matches ? 2 : 1);
						};
						mount(); (mq.addEventListener ? mq.addEventListener('change', mount) : mq.addListener(mount));
					});
				}
				// time
				const timeSet = new Set((timeHidden.value || '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean));
				modal.querySelectorAll('[data-modal-time]').forEach((c) => {
					c.checked = timeSet.has(c.value);
				});
				// types
				const chosenTypes = new Set(
					Array.from(form.querySelectorAll('input[name="type"]:checked, input[name="type[]"]:checked'))
						.map((i) => (i.value || i.dataset.key || '').trim())
						.filter(Boolean)
				);
				modal.querySelectorAll('[data-modal-type]').forEach((c) => {
					const key = (c.value || c.dataset.key || '').trim();
					c.checked = chosenTypes.has(key);
				});
			}
		}
	});

	// Modal APPLY / CANCEL
	bar.addEventListener('click', (e) => {
		const btnApply = e.target.closest('[data-submit]');
		const btnCancel = e.target.closest('[data-cancel]');
		if (!btnApply && !btnCancel) {
			return;
		}

		const modalEl = (btnApply || btnCancel)?.closest('dialog[data-modal="all"]');
		if (modalEl) {
			if (btnCancel) {
				const mode = btnCancel.getAttribute('data-cancel-mode') || 'cancel';
				if (mode === 'clear') {
					modalEl.querySelectorAll('[data-modal-time]').forEach((c) => c.checked = false);
					modalEl.querySelectorAll('[data-modal-type]').forEach((c) => c.checked = false);
					const mFrom = modalEl.querySelector('[data-modal-from]'); if (mFrom) {
						mFrom.value = '';
					}
					const mTo = modalEl.querySelector('[data-modal-to]'); if (mTo) {
						mTo.value = '';
					}
					// reflect to inline
					form.querySelectorAll('input[name="type"], input[name="type[]"]').forEach((i) => i.checked = false);
					form.querySelector('input[name="from"]')?.setAttribute('value', '');
					form.querySelector('input[name="to"]')?.setAttribute('value', '');
					writeTimeCSV([]);
					paintChoiceActives(form); setAllTriggersFromForm();
					modalEl.close?.(); localRenderAndUi(true);
					return;
				}
				modalEl.close?.(); return;
			}

			// APPLY
			const mFrom = modalEl.querySelector('[data-modal-from]');
			const mTo = modalEl.querySelector('[data-modal-to]');
			const from = form.querySelector('input[name="from"]');
			const to = form.querySelector('input[name="to"]');
			if (from) {
				from.value = mFrom?.value || '';
			}
			if (to) {
				to.value = mTo?.value || '';
			}

			const selectedTimes = Array.from(modalEl.querySelectorAll('[data-modal-time]:checked')).map((i) => i.value);
			writeTimeCSV(selectedTimes);

			const selectedTypes = new Set(Array.from(modalEl.querySelectorAll('[data-modal-type]:checked'))
				.map((i) => (i.value || i.dataset.key || '').trim()).filter(Boolean));
			form.querySelectorAll('input[name="type"], input[name="type[]"]').forEach((i) => {
				const key = (i.value || i.dataset.key || '').trim();
				i.checked = selectedTypes.has(key);
			});

			e.stopImmediatePropagation();
			paintChoiceActives(form); setAllTriggersFromForm();
			modalEl.close?.(); localRenderAndUi(true);
			return;
		}

		// TIME popover
		const timePanel = (btnApply || btnCancel)?.closest('[data-panel="time"]');
		if (timePanel) {
			if (btnCancel) {
				const mode = btnCancel.getAttribute('data-cancel-mode') || 'cancel';
				if (mode === 'clear') {
					timePanel.querySelectorAll('input[name="time[]"]').forEach((i) => i.checked = false);
					writeTimeCSV([]); paintChoiceActives(form); setAllTriggersFromForm();
					localRenderAndUi(true);
				}
				timePanel.setAttribute('hidden', '');
				return;
			}
			if (btnApply) {
				const checked = Array.from(timePanel.querySelectorAll('input[name="time[]"]:checked')).map((i) => i.value);
				writeTimeCSV(checked); paintChoiceActives(form); setAllTriggersFromForm();
				timePanel.setAttribute('hidden', ''); localRenderAndUi(true);
			}
		}
	}, { capture: true });

	// **INLINE TYPE CHIPS – instant apply**
	form.addEventListener('change', (e) => {
		if (e.target.matches('input[name="type"], input[name="type[]"]')) {
			paintChoiceActives(form);
			const scope = e.target.closest('dialog, .popover__panel') || bar;
			if (scope) {
				markScopeDirty(scope, true);
			} // if inside modal keep Apply state
			// But on the page we apply instantly for inline chips:
			if (!scope || !scope.matches('dialog')) {
				localRenderAndUi(true);
			}
			updateAllFiltersCount(bar, form, { multiCsvKeys: new Set(['time', 'type']), perItemKeys: new Set(['type']) });
			setAllTriggersFromForm();
		}
		// Sort is NOT instant
		if (e.target.matches('select[name="sort"], input[name="sort"]')) {
			const scope = e.target.closest('dialog, .popover__panel') || bar;
			if (scope) {
				markScopeDirty(scope, true);
			}
		}
	});

	// reflect inline date edits
	form.querySelectorAll('input[name="from"], input[name="to"]').forEach((i) => {
		const sync = () => {
			updateDateChipActive();
			const modal = bar.querySelector('dialog[data-modal="all"]');
			if (modal?.open) {
				syncInlineDatesToModal(modal);
			}
		};
		i.addEventListener('input', sync);
		i.addEventListener('change', sync);
	});

	// search debounce
	const q = form.querySelector('input[name="q"], #q');
	if (q) {
		const apply = debounce(() => localRenderAndUi(true), 350);
		q.addEventListener('input', apply);
		q.addEventListener('change', apply);
	}

	// popstate
	window.addEventListener('popstate', () => {
		seedFormFromURL(form, { multi: new Set(['time', 'type']), defaults: DEFAULTS });
		paintChoiceActives(form); updateDateChipActive(); setAllTriggersFromForm();
		localRenderAndUi(false);
	});

	// first paint
	paintChoiceActives(document);
	updateDateChipActive(); setAllTriggersFromForm();
	// Baseline the seeded state before the first paint's track() call diffs it.
	filterTracker.init(trackParams());
	syncAllFiltersButton();
	localRenderAndUi(true);
}